import * as React from 'react';
const { useState } = React;
const h = React.createElement;
import { icons } from '../icons';

export interface TabItem {
  key: React.Key;
  label?: React.ReactNode;
  children?: React.ReactNode;
  closable?: boolean;
}

export interface TabsProps {
  activeKey?: React.Key;
  onChange?: (key: React.Key) => void;
  items?: TabItem[];
  type?: 'line' | 'editable-card';
  onEdit?: (key: React.Key | null, action: 'add' | 'remove') => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

interface TabsComponent extends React.FC<TabsProps> {
  TabPane: React.FC<{ tab?: React.ReactNode; closable?: boolean; children?: React.ReactNode }>;
}

const TabsBase: React.FC<TabsProps> = function Tabs(props) {
  const { activeKey, onChange, items = [], type, onEdit, className = '', children } = props;

  // Support legacy children-based API (TabPane)
  let tabItems: TabItem[] = items;
  if ((!items || items.length === 0) && children) {
    const collected: TabItem[] = [];
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const c = child as React.ReactElement<{
          tab?: React.ReactNode;
          children?: React.ReactNode;
          closable?: boolean;
        }>;
        if (c.key != null) {
          collected.push({
            key: c.key,
            label: c.props.tab,
            children: c.props.children,
            closable: c.props.closable,
          });
        }
      }
    });
    tabItems = collected;
  }

  const [internalActive, setInternalActive] = useState<React.Key | undefined>(tabItems[0]?.key);
  const currentKey = activeKey !== undefined ? activeKey : internalActive;

  const isEditableCard = type === 'editable-card';

  return h(
    'div',
    { className: `cb-tabs-root ${className}` },
    h(
      'div',
      { className: 'cb-tabs-header flex items-center border-b border-dark-700 mb-0' },
      h(
        'div',
        { className: 'flex items-center gap-1 overflow-x-auto' },
        tabItems.map((item) =>
          h(
            'div',
            {
              key: item.key,
              className: `flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors border-b-2 whitespace-nowrap ${item.key === currentKey ? 'text-primary-400 border-primary-500' : 'text-dark-400 border-transparent hover:text-dark-200'}`,
              onClick: () => {
                setInternalActive(item.key);
                if (onChange) onChange(item.key);
              },
            },
            item.label,
            isEditableCard &&
              item.closable !== false &&
              h(
                'span',
                {
                  className: 'ml-1 text-dark-500 hover:text-red-400 transition-colors p-1 -mr-2',
                  onClick: (e: React.MouseEvent<HTMLSpanElement>) => {
                    e.stopPropagation();
                    if (onEdit) onEdit(item.key, 'remove');
                  },
                },
                h(icons.CloseCircleOutlined, { size: '1em' }),
              ),
          ),
        ),
      ),
      isEditableCard &&
        h(
          'button',
          {
            className: 'ml-2 p-1.5 text-dark-500 hover:text-primary-400 transition-colors',
            onClick: () => {
              if (onEdit) onEdit(null, 'add');
            },
          },
          h(icons.PlusOutlined, { size: '0.85em' }),
        ),
    ),
    h(
      'div',
      { className: 'cb-tabs-body' },
      tabItems.map((item) =>
        h(
          'div',
          {
            key: item.key,
            className: 'cb-tabs-pane',
            style: { display: item.key === currentKey ? 'flex' : 'none' },
          },
          item.children,
        ),
      ),
    ),
  );
};

const Tabs = TabsBase as TabsComponent;

Tabs.TabPane = function TabPane(props) {
  return h(React.Fragment, null, props.children) as React.ReactElement;
};

export { Tabs };
